/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import Grid from "@mui/material/Grid2";
import FormHelperText from "@mui/material/FormHelperText";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import Typography from "@mui/material/Typography";

import GridSectionHeader from "../../widgets/GridSectionHeader";
import GridTextfield from "../../widgets/GridTextfield";

import {
  valudateTextfield2,
  validateCurrencyField2,
  validateCurrencyField3,
  removeCommaFromCurrency,
  convertLocaleString,
} from "../../../utils/fieldValidator.js";

import { updateLongTermCare } from "../../../states/slcObjectives.js";

export default function LongTermCare() {
  const longTermCare = useSelector(
    (state) => state.objectives.protection.longTermCare
  );

  const [ltcNeedsMonthly, setLtcNeedsMonthly] = useState(
    longTermCare.ltcNeedsMonthly
  );
  const [ltcPreferredPayoutPeriod, setLtcPreferredPayoutPeriod] = useState(
    longTermCare.ltcPreferredPayoutPeriod
  );
  const [ltcCurrBenefitsMonthly, setLtcCurrBenefitsMonthly] = useState(
    longTermCare.ltcCurrBenefitsMonthly
  );
  const [ltcSurplysShortfall, setLtcSurplysShortfall] = useState(
    longTermCare.ltcSurplysShortfall
  );

  const [deviationReason, setDeviationReason] = useState(
    longTermCare.deviationReason
  );
  const [deviationReasonError, setDeviationReasonError] = useState();

  const dispatch = useDispatch();

  //COMPONENT ID
  let id = "";

  /**==============================
   * EFFECTS
   ==============================*/
  useEffect(calcSurplusOrShortfall, [ltcCurrBenefitsMonthly, ltcNeedsMonthly]);

  /**==============================
   * FUNCTIONS
   ==============================*/
  function calcSurplusOrShortfall() {
    let surplusShortfall =
      Number(removeCommaFromCurrency(ltcCurrBenefitsMonthly)) -
      Number(removeCommaFromCurrency(ltcNeedsMonthly));

    surplusShortfall = convertLocaleString(surplusShortfall, "en-US", 2);

    updateRedux("ltcSurplysShortfall", surplusShortfall, updateLongTermCare);
    setLtcSurplysShortfall(surplusShortfall);
  }

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, object) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    //TEXTFIELDS
    id = e.target.id;
    validateFields(e, id, input_data);
  }

  function validateFields(e, id, input_data) {
    if (id === "ltcNeedsMonthly") {
      input_data.fieldName = "Monthly needs";
      input_data.fieldVariable = ltcNeedsMonthly;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLtcNeedsMonthly(result.value);
        updateRedux("ltcNeedsMonthly", result.value, updateLongTermCare);
      }
    }

    if (id === "ltcPreferredPayoutPeriod") {
      input_data.fieldName = "Years";
      input_data.fieldVariable = ltcPreferredPayoutPeriod;
      const result = validateCurrencyField2(input_data);
      if (result.errorMessage === "") {
        setLtcPreferredPayoutPeriod(result.value);
        updateRedux(
          "ltcPreferredPayoutPeriod",
          result.value,
          updateLongTermCare
        );
      }
    }

    if (id === "ltcCurrBenefitsMonthly") {
      input_data.fieldName = "Current monthly benefits";
      input_data.fieldVariable = ltcCurrBenefitsMonthly;
      const result = validateCurrencyField3(input_data);
      if (result.errorMessage === "") {
        setLtcCurrBenefitsMonthly(result.value);
        updateRedux("ltcCurrBenefitsMonthly", result.value, updateLongTermCare);
      }
    }

    if (id === "deviationReason") {
      input_data.fieldName = "Deviation reason";
      input_data.fieldLength = 1000;
      input_data.fieldVariable = deviationReason;
      const result = valudateTextfield2(input_data);
      if (result.errorMessage === "") {
        setDeviationReason(result.value);
        updateRedux("deviationReason", result.value, updateLongTermCare);
      }

      setDeviationReasonError(result.errorMessage);
    }
  }

  return (
    <>
      <Grid container spacing={2} size={12}>
        <GridSectionHeader
          sectionName="In the Event of Disability (Long Term Care / Disability Income)"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (A) Monthly Needs Required
          </Typography>
        </Grid>
        <GridTextfield
          id="ltcNeedsMonthly"
          key="ltcNeedsMonthly"
          columnSize="3"
          label="Monthly"
          isCurrency={true}
          currState={ltcNeedsMonthly}
          onChange={handleChange}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            Preferred Benefit Payout Period (Lifetime or Years)
          </Typography>
        </Grid>
        <GridTextfield
          id="ltcPreferredPayoutPeriod"
          key="ltcPreferredPayoutPeriod"
          columnSize="3"
          label="Years"
          currState={ltcPreferredPayoutPeriod}
          onChange={handleChange}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (B) Current Monthly Benefits{" "}
          </Typography>
        </Grid>
        <GridTextfield
          id="ltcCurrBenefitsMonthly"
          key="ltcCurrBenefitsMonthly"
          columnSize="3"
          label="Monthly"
          isCurrency={true}
          currState={ltcCurrBenefitsMonthly}
          onChange={handleChange}
        />

        <Grid size={9}>
          <Typography
            variant="body1"
            sx={{
              pt: 2.0,
            }}
          >
            (B - A) = Surplus / (Shortfall)
          </Typography>
        </Grid>
        <GridTextfield
          id="ltcSurplysShortfall"
          key="ltcSurplysShortfall"
          columnSize="3"
          isCurrency={true}
          currState={ltcSurplysShortfall}
          onChange={handleChange}
          disabled={true}
        />

        <Typography variant="body1">Reason(s) for Deviations:</Typography>
        <TextareaAutosize
          id="deviationReason"
          placeholder="Type here..."
          style={{ width: "100%" }}
          minRows={5}
          onChange={handleChange}
          defaultValue={deviationReason}
        />
        {deviationReasonError && (
          <FormHelperText error={deviationReasonError}>
            {deviationReasonError}
          </FormHelperText>
        )}
      </Grid>
    </>
  );
}
